import styles from "./Avatar.module.css";

interface AvatarProps {
  name?: string;
  image?: string;
  size?: number;
  /**
   * When false (or `variant="neutral"`), the fallback uses a neutral accent
   * tint instead of a per-user gradient — useful for "default" empty avatars
   * in popups and placeholders.
   */
  gradient?: boolean;
  variant?: "gradient" | "neutral";
}

// 8 gradient pairs — assigned deterministically per name. Mockup section 16.
const PALETTE = [
  "linear-gradient(135deg, #10b981, #0f766e)",
  "linear-gradient(135deg, #f97316, #ef4444)",
  "linear-gradient(135deg, #3b82f6, #1e40af)",
  "linear-gradient(135deg, #a855f7, #6b21a8)",
  "linear-gradient(135deg, #f59e0b, #b45309)",
  "linear-gradient(135deg, #ec4899, #be185d)",
  "linear-gradient(135deg, #14b8a6, #0e7490)",
  "linear-gradient(135deg, #84cc16, #4d7c0f)",
];

function hashName(name: string): number {
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum = (sum + name.charCodeAt(i) * (i + 1)) >>> 0;
  }
  return sum;
}

export function gradientFor(name: string): string {
  return PALETTE[hashName(name) % PALETTE.length];
}

export function initialsFor(name: string | undefined): string {
  const n = (name ?? "").trim();
  if (!n) return "?";
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return n.slice(0, 2).toUpperCase();
}

export function Avatar({
  name,
  image,
  size = 32,
  gradient,
  variant,
}: AvatarProps) {
  if (image) {
    return (
      <img
        src={image}
        width={size}
        height={size}
        className={styles.avatar}
        alt=""
      />
    );
  }
  const useNeutral = gradient === false || variant === "neutral";
  const initials = initialsFor(name);
  // Initials font-size scales to ~42% of avatar size — keeps readable across
  // the 24/28/32/36/48 presets.
  const fontSize = Math.max(10, Math.round(size * 0.42));
  const style: React.CSSProperties = {
    width: size,
    height: size,
    fontSize,
  };
  if (useNeutral) {
    return (
      <div
        className={`${styles.fallback} ${styles.neutral}`}
        style={style}
        aria-hidden
      >
        {initials}
      </div>
    );
  }
  return (
    <div
      className={`${styles.fallback} ${styles.gradient}`}
      style={{ ...style, background: gradientFor(name ?? "") }}
      aria-hidden
    >
      {initials}
    </div>
  );
}
